import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ChevronDown, ExternalLink, Play, Search, X } from 'lucide-react'
import { apiFetch, qs } from '../lib/api'
import { useDebounced } from '../lib/hooks'
import type { CheckoutJourney, JourneyEvent, JourneyStage, JourneySummary, Paged, PersonHistory } from '../lib/types'
import { DataTable } from '../components/data-table'
import { Pagination } from '../components/pagination'
import { PageHeader, formatDate } from '../components/page'
import { ErrorState, Spinner } from '../components/states'
import { Badge, StatusBadge, type Tone } from '../components/badge'
import { ReplayPlayer } from '../components/replay-player'

// Where visitors come from and how far they get in the checkout. Nothing is
// stored by us: the backend reads Rybbit (visits, sources, checkout events),
// and for one person also Brevo and Chargebee, live on each request.

const RANGES = [
	{ days: 1, label: 'Today' },
	{ days: 7, label: 'Last 7 days' },
	{ days: 30, label: 'Last 30 days' },
	{ days: 90, label: 'Last 90 days' },
]

const STAGE_LABELS: Record<JourneyStage, string> = {
	browsing: 'Browsed',
	checkout: 'Opened checkout',
	email: 'Entered email',
	payment: 'Reached payment',
	paid: 'Paid',
}

const STAGE_TONES: Record<JourneyStage, Tone> = {
	browsing: 'neutral',
	checkout: 'info',
	email: 'warning',
	payment: 'warning',
	paid: 'success',
}

const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—')
const num = (n: number) => n.toLocaleString()
const stageFilterLabel = (s: JourneyStage) =>
	s === 'paid' ? 'Paid' : s === 'browsing' ? 'Only browsed (no checkout)' : `Stopped at: ${STAGE_LABELS[s]}`
const formatDay = (day: string) => new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { dateStyle: 'medium' })

export function CustomerJourneyPage() {
	const [days, setDays] = useState(30)
	const [source, setSource] = useState('')
	const [stage, setStage] = useState('')
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const [selected, setSelected] = useState<{ visit: CheckoutJourney; view: DetailView } | null>(null)
	const q = useDebounced(search)

	const summary = useQuery({
		queryKey: ['journey-summary', days],
		queryFn: () => apiFetch<JourneySummary>(`/api/admin/analytics/summary${qs({ days })}`),
	})

	const visits = useQuery({
		queryKey: ['journey-visits', days, source, stage, q, page],
		queryFn: () => apiFetch<Paged<CheckoutJourney>>(`/api/admin/analytics/sessions${qs({ days, source, stage, q, page })}`),
	})

	function reset<T>(setter: (v: T) => void) {
		return (value: T) => {
			setter(value)
			setPage(1)
		}
	}

	return (
		<>
			<PageHeader
				title="Customer Journey"
				subtitle="Where visitors come from and how far they get in the checkout — live from Rybbit, Brevo and Chargebee."
				actions={
					<>
						{summary.data?.rybbitUrl && (
							<a className="btn-secondary" href={summary.data.rybbitUrl} target="_blank" rel="noreferrer">
								Open Rybbit <ExternalLink className="h-3.5 w-3.5" />
							</a>
						)}
						<select className="input w-auto" value={days} onChange={(e) => reset(setDays)(Number(e.target.value))}>
							{RANGES.map((r) => (
								<option key={r.days} value={r.days}>{r.label}</option>
							))}
						</select>
					</>
				}
			/>

			<div id="journey-visits" className="card">
				<div className="border-b border-line px-4 pt-4">
					<h2 className="font-display text-base font-semibold">Visits</h2>
					<p className="pb-3 text-xs text-ink-soft">
						Every visit from its first page{summary.data?.trackingSince ? ` since ${formatDay(summary.data.trackingSince)}` : ''}, and how far it got — most visitors only browse. Click one to see its full path.
					</p>
				</div>
				<div className="flex flex-wrap gap-2 border-b border-line p-3">
					<div className="relative min-w-0 flex-1 sm:max-w-xs">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
						<input className="input pl-9" placeholder="Search email" value={search} onChange={(e) => reset(setSearch)(e.target.value)} />
					</div>
					<select className="input w-auto" value={source} onChange={(e) => reset(setSource)(e.target.value)}>
						<option value="">All sources</option>
						{summary.data?.bySource.map((r) => (
							<option key={r.source} value={r.source}>{r.source}</option>
						))}
					</select>
					<select className="input w-auto" value={stage} onChange={(e) => reset(setStage)(e.target.value)}>
						<option value="">Any outcome</option>
						{(Object.keys(STAGE_LABELS) as JourneyStage[]).map((s) => (
							<option key={s} value={s}>{stageFilterLabel(s)}</option>
						))}
					</select>
				</div>

				{summary.data?.capped && (
					<p className="border-b border-line bg-amber-50 px-3 py-2 text-xs text-amber-800">
						This period has more visits than the page loads at once; only the newest 20,000 are listed and counted. Choose a shorter period for exact numbers.
					</p>
				)}

				{(source || stage || search) && (
					<div className="flex flex-wrap items-center gap-2 border-b border-line bg-canvas/60 px-3 py-2 text-xs">
						<span className="text-ink-soft">Showing only:</span>
						{source && <FilterChip label={`Source: ${source}`} onClear={() => reset(setSource)('')} />}
						{stage && (
							<FilterChip
								label={stageFilterLabel(stage as JourneyStage)}
								onClear={() => reset(setStage)('')}
							/>
						)}
						{search && <FilterChip label={`Email contains "${search}"`} onClear={() => reset(setSearch)('')} />}
						<button
							type="button"
							onClick={() => {
								setSource('')
								setStage('')
								setSearch('')
								setPage(1)
							}}
							className="ml-auto cursor-pointer font-medium text-ink underline underline-offset-2 hover:opacity-70"
						>
							Clear all filters
						</button>
					</div>
				)}

				{visits.isPending ? (
					<Spinner label="Loading from Rybbit…" />
				) : visits.error ? (
					<ErrorState error={visits.error} onRetry={visits.refetch} />
				) : (
					<>
						<DataTable
							columns={visitColumns((visit) => setSelected({ visit, view: 'replay' }))}
							rows={visits.data.rows}
							emptyMessage={
								summary.data && !summary.data.trackingSince
									? 'Nothing tracked yet. Visits appear here once the new checkout tracking is live on the website.'
									: 'No visits match those filters.'
							}
							onRowClick={(visit) => setSelected({ visit, view: 'visit' })}
						/>
						<Pagination page={visits.data.page} limit={visits.data.limit} total={visits.data.total} onPageChange={setPage} />
					</>
				)}
			</div>

			{summary.isPending ? (
				<div className="card mt-5"><Spinner label="Loading from Rybbit…" /></div>
			) : summary.error ? (
				<div className="card mt-5"><ErrorState error={summary.error} onRetry={summary.refetch} /></div>
			) : (
				<>
					<FunnelCard totals={summary.data.totals} since={summary.data.trackingSince} />
					<SourcesCard
						rows={summary.data.bySource}
						selected={source}
						onPick={(s) => {
							// Clicking the source that is already selected clears the filter.
							reset(setSource)(s === source ? '' : s)
							document.getElementById('journey-visits')?.scrollIntoView({ behavior: 'smooth' })
						}}
					/>
				</>
			)}

			{selected && <VisitDetail visit={selected.visit} initialView={selected.view} onClose={() => setSelected(null)} />}
		</>
	)
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
	return (
		<span className="inline-flex items-center gap-1 rounded-full border border-line bg-surface py-0.5 pl-2.5 pr-1 font-medium text-ink">
			{label}
			<button
				type="button"
				onClick={onClear}
				aria-label={`Remove filter ${label}`}
				className="inline-flex h-4 w-4 cursor-pointer items-center justify-center rounded-full text-ink-soft hover:bg-canvas hover:text-ink"
			>
				<X className="h-3 w-3" />
			</button>
		</span>
	)
}

// Each step counts the visits that got that far. Pricing is shown for
// context but is not a required step: many visitors open the checkout straight
// from a blog post, so the checkout count can exceed it.
const FUNNEL_STEPS: { key: keyof JourneySummary['totals']; label: string; hint: string }[] = [
	{ key: 'visits', label: 'Visited the site', hint: 'All visits' },
	{ key: 'pricing', label: 'Viewed pricing', hint: 'Opened a pricing page' },
	{ key: 'checkout', label: 'Opened checkout', hint: 'Opened the checkout page' },
	{ key: 'email', label: 'Entered email', hint: 'Typed their email in the checkout' },
	{ key: 'payment', label: 'Reached payment', hint: 'Got to the payment step' },
	{ key: 'paid', label: 'Paid', hint: 'Completed the purchase' },
]

function FunnelCard({ totals, since }: { totals: JourneySummary['totals']; since: string | null }) {
	return (
		<div className="card mt-5 p-4">
			<h2 className="font-display text-base font-semibold">Funnel</h2>
			<p className="mb-4 text-xs text-ink-soft">
				{since
					? `How many visits reached each step since ${formatDay(since)}, when the new checkout tracking started, and how many were lost since the step before.`
					: 'Nothing tracked yet — the funnel fills in once the new checkout tracking is live on the website.'}
			</p>
			<ol className="flex flex-col gap-3">
				{FUNNEL_STEPS.map((step, i) => {
					const value = totals[step.key]
					const prev = i > 0 ? totals[FUNNEL_STEPS[i - 1].key] : null
					const lost = prev !== null && step.key !== 'pricing' && prev > value ? prev - value : 0
					const share = totals.visits > 0 ? value / totals.visits : 0
					return (
						<li key={step.key} className="grid grid-cols-[minmax(0,8.5rem)_1fr] items-center gap-x-3 gap-y-1 sm:grid-cols-[10rem_1fr_12rem]">
							<span className="text-sm" title={step.hint}>{step.label}</span>
							<div className="h-6 rounded bg-canvas" title={`${step.label}: ${num(value)} (${pct(value, totals.visits)} of visits)`}>
								<div className="h-full rounded bg-brand" style={{ width: `${value > 0 ? Math.max(share * 100, 0.6) : 0}%` }} />
							</div>
							<span className="col-start-2 text-xs text-ink-soft tabular-nums sm:col-start-auto">
								<span className="font-medium text-ink">{num(value)}</span> · {pct(value, totals.visits)} of visits
								{lost > 0 && <span className="block sm:inline"> · {num(lost)} lost</span>}
							</span>
						</li>
					)
				})}
			</ol>
		</div>
	)
}

function SourcesCard({ rows, selected, onPick }: { rows: JourneySummary['bySource']; selected: string; onPick: (source: string) => void }) {
	const count = (key: 'visits' | 'checkout' | 'email' | 'payment' | 'paid', header: string): ColumnDef<JourneySummary['bySource'][number], any> => ({
		header,
		accessorKey: key,
		cell: ({ getValue }) => <span className="tabular-nums">{num(getValue<number>())}</span>,
	})
	const columns: ColumnDef<JourneySummary['bySource'][number], any>[] = [
		{ header: 'Source', accessorKey: 'source', cell: ({ getValue }) => <span className="font-medium">{getValue<string>()}</span> },
		count('visits', 'Visits'),
		count('checkout', 'Opened checkout'),
		count('email', 'Entered email'),
		count('payment', 'Reached payment'),
		count('paid', 'Paid'),
	]
	return (
		<div className="card mt-5">
			<div className="flex flex-wrap items-start justify-between gap-2 border-b border-line px-4 py-3">
				<div>
					<h2 className="font-display text-base font-semibold">By source</h2>
					<p className="text-xs text-ink-soft">
						{selected
							? `Visits are filtered to ${selected}. Click it again, or "Show all sources", to see everyone.`
							: 'Click a source to show only its visits in the list above.'}
					</p>
				</div>
				{selected && (
					<button type="button" onClick={() => onPick(selected)} className="btn-secondary">
						Show all sources
					</button>
				)}
			</div>
			<DataTable
				columns={columns}
				rows={rows}
				emptyMessage="No visits in this period."
				onRowClick={(r) => onPick(r.source)}
				isRowSelected={(r) => r.source === selected}
			/>
		</div>
	)
}

// Local and preview testing also reaches Rybbit; flag it so it is never read
// as a real customer.
const isTestHost = (host: string | null) => Boolean(host) && !/(^|\.)cloudbasedbackup\.com$/.test(host as string)

const visitColumns = (onWatch: (visit: CheckoutJourney) => void): ColumnDef<CheckoutJourney, any>[] => [
	{ header: 'Started', accessorKey: 'startedAt', cell: ({ getValue }) => <span className="whitespace-nowrap">{formatDate(getValue<string>())}</span> },
	{
		header: 'Source',
		cell: ({ row }) => (
			<div className="min-w-0">
				<span className="font-medium">{row.original.source}</span>
				{isTestHost(row.original.hostname) && (
					<span className="ml-1.5" title={`Recorded on ${row.original.hostname}, not the live site`}>
						<Badge tone="warning">{row.original.hostname}</Badge>
					</span>
				)}
				{row.original.utmCampaign && <span className="block text-xs text-ink-soft">{row.original.utmCampaign}</span>}
			</div>
		),
	},
	{ header: 'Landed on', accessorKey: 'landingPath', cell: ({ getValue }) => <span className="mono block max-w-xs truncate">{getValue<string>() ?? '—'}</span> },
	{ header: 'Pages', accessorKey: 'pageviews', cell: ({ getValue }) => <span className="tabular-nums">{getValue<number>()}</span> },
	{ header: 'Got to', cell: ({ row }) => <Badge tone={STAGE_TONES[row.original.furthest]}>{STAGE_LABELS[row.original.furthest]}</Badge> },
	{ header: 'Email', accessorKey: 'email', cell: ({ getValue }) => <span className="block max-w-[14rem] truncate">{getValue<string>() ?? '—'}</span> },
	{ header: 'Device', accessorKey: 'device', cell: ({ getValue }) => <span className="text-ink-soft">{getValue<string>() ?? '—'}</span> },
	{
		header: 'Replay',
		cell: ({ row }) =>
			row.original.hasReplay ? (
				<button
					type="button"
					// The row itself opens the timeline; this opens the recording.
					onClick={(e) => {
						e.stopPropagation()
						onWatch(row.original)
					}}
					className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-line px-2 py-1 text-xs font-medium hover:bg-canvas"
				>
					<Play className="h-3 w-3" /> Watch
				</button>
			) : (
				<span className="whitespace-nowrap text-xs text-ink-faint" title="Rybbit has no recording of this visit">
					No recording
				</span>
			),
	},
]

// The visitor's page in Rybbit (all their visits, events and replays there).
function RybbitLink({ visit }: { visit: CheckoutJourney }) {
	if (!visit.rybbitUrl) return null
	return (
		<a href={visit.rybbitUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-ink">
			Open in Rybbit <ExternalLink className="h-3 w-3" />
		</a>
	)
}

// --- Timeline -----------------------------------------------------------------
// Modelled on a Brevo contact's History tab: one dot per event on a vertical
// rule, a plain-language title, the raw event name, when it happened, and the
// event's fields behind "Show all event details".

type Marker = 'neutral' | 'progress' | 'success' | 'danger'

const MARKER_CLASSES: Record<Marker, string> = {
	neutral: 'bg-ink-faint',
	progress: 'bg-sky-600',
	success: 'bg-emerald-600',
	danger: 'bg-red-600',
}

interface TimelineEntry {
	id: string
	name: string
	path: string | null
	props: Record<string, unknown> | null
	email: string | null
	createdAt: string
}

function present(e: TimelineEntry): { title: string; detail?: string; marker: Marker } {
	const p = e.props ?? {}
	switch (e.name) {
		case 'pageview':
			return { title: 'Viewed a page', detail: e.path ?? undefined, marker: 'neutral' }
		case 'checkout_opened':
			return { title: 'Opened the checkout', detail: p.plan ? `${p.plan} · ${p.billing}` : undefined, marker: 'progress' }
		case 'checkout_started':
		case 'checkout_email_entered':
			return { title: 'Entered their email', detail: e.email ?? undefined, marker: 'progress' }
		case 'checkout_step':
			return { title: `Moved to the ${String(p.step).replace(/^\d+\.\s*/, '').toLowerCase()} step`, marker: 'progress' }
		case 'checkout_left':
		case 'checkout_abandoned':
			return { title: 'Left the checkout', detail: `At the ${String(p.step).replace(/^\d+\.\s*/, '').toLowerCase()} step, without paying`, marker: 'danger' }
		case 'purchase':
		case 'checkout_completed':
			return { title: 'Paid', detail: p.plan ? `${p.plan} · ${p.billing}` : undefined, marker: 'success' }
		default:
			return { title: e.name, marker: 'neutral' }
	}
}

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

function timeAgo(value: string): string {
	const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000)
	const steps: [Intl.RelativeTimeFormatUnit, number][] = [
		['year', 31536000],
		['month', 2592000],
		['day', 86400],
		['hour', 3600],
		['minute', 60],
	]
	for (const [unit, size] of steps) {
		if (Math.abs(seconds) >= size) return relative.format(Math.round(seconds / size), unit)
	}
	return relative.format(seconds, 'second')
}

function TimelineItem({ event }: { event: TimelineEntry }) {
	const [open, setOpen] = useState(false)
	const { title, detail, marker } = present(event)
	const fields = Object.entries({ path: event.path, ...(event.props ?? {}) }).filter(
		([, v]) => v !== null && v !== undefined && v !== '',
	)

	return (
		<li className="relative pb-5 pl-6 last:pb-0">
			{/* Ring in the surface colour so the dot punches through the rule. */}
			<span className={`absolute -left-[6px] top-1 h-3 w-3 rounded-full ring-4 ring-surface ${MARKER_CLASSES[marker]}`} />
			<p className="text-sm font-semibold">{title}</p>
			{detail && <p className="mt-0.5 break-words text-sm">{detail}</p>}
			<p className="mt-0.5 text-xs text-ink-soft" title={formatDate(event.createdAt)}>
				{timeAgo(event.createdAt)} · <span className="mono">{event.name}</span>
			</p>
			{fields.length > 0 && (
				<>
					<button
						type="button"
						aria-expanded={open}
						onClick={() => setOpen((v) => !v)}
						className="mt-1 inline-flex cursor-pointer items-center gap-1 text-xs text-ink underline underline-offset-2"
					>
						{open ? 'Hide event details' : 'Show all event details'}
						<ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
					</button>
					{open && (
						<dl className="mt-2 rounded-lg bg-canvas p-3 text-xs">
							{fields.map(([key, value]) => (
								<div key={key} className="flex gap-2 py-0.5">
									<dt className="shrink-0 text-ink-soft">{key}:</dt>
									<dd className="mono min-w-0 break-words">{JSON.stringify(value)}</dd>
								</div>
							))}
						</dl>
					)}
				</>
			)}
		</li>
	)
}

function Timeline({ events }: { events: TimelineEntry[] }) {
	if (events.length === 0) return <p className="text-sm text-ink-soft">No events recorded.</p>
	return (
		<ol className="ml-1.5 border-l border-line">
			{events.map((e) => (
				<TimelineItem key={e.id} event={e} />
			))}
		</ol>
	)
}

function SectionError({ message }: { message: string }) {
	return <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{message}</p>
}

// Brevo and Chargebee are looked up by the email typed in the checkout; one
// request answers both tabs (react-query shares it between them).
function usePerson(email: string) {
	return useQuery({
		queryKey: ['journey-person', email],
		queryFn: () => apiFetch<PersonHistory>(`/api/admin/analytics/people${qs({ email })}`),
	})
}

// The contact's checkout history as Brevo recorded it (these events drive the
// abandoned-checkout emails), plus its checkout fields.
function BrevoTab({ email }: { email: string }) {
	const { data, isPending, error, refetch } = usePerson(email)
	if (isPending) return <Spinner label="Loading from Brevo…" />
	if (error) return <ErrorState error={error} onRetry={refetch} />
	if (!data.brevo.ok) return <SectionError message={data.brevo.error} />
	if (!data.brevo.data) return <p className="text-sm text-ink-soft">{email} is not a Brevo contact.</p>

	const { attributes, events } = data.brevo.data
	return (
		<div className="flex flex-col gap-4">
			<p className="text-xs text-ink-soft">
				Every checkout event Brevo has for {email}, across all their visits — the same data that triggers the abandoned-checkout
				emails.
			</p>
			{Object.keys(attributes).length > 0 && (
				<dl className="grid grid-cols-2 gap-2 rounded-lg bg-canvas p-3 text-xs">
					{Object.entries(attributes).map(([k, v]) => (
						<div key={k} className="min-w-0">
							<dt className="text-ink-soft">{k}</dt>
							<dd className="mono truncate">{String(v)}</dd>
						</div>
					))}
				</dl>
			)}
			<Timeline
				events={events.map((e, i) => ({ id: `brevo-${i}`, name: e.name, path: null, props: e.props, email: null, createdAt: e.date }))}
			/>
		</div>
	)
}

// Whether the person really paid: their subscriptions in Chargebee.
function ChargebeeTab({ email }: { email: string }) {
	const { data, isPending, error, refetch } = usePerson(email)
	if (isPending) return <Spinner label="Loading from Chargebee…" />
	if (error) return <ErrorState error={error} onRetry={refetch} />
	if (!data.chargebee.ok) return <SectionError message={data.chargebee.error} />
	if (data.chargebee.data.subscriptions.length === 0) {
		return <p className="text-sm text-ink-soft">No subscription in Chargebee — {email} has not paid.</p>
	}
	return (
		<ul className="flex flex-col gap-2">
			{data.chargebee.data.subscriptions.map((s) => (
				<li key={s.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm">
					<StatusBadge value={s.status} />
					<span className="mono">{s.plan ?? s.id}</span>
					<span className="text-xs text-ink-soft">{formatDate(s.createdAt)}</span>
					{s.url && (
						<a className="ml-auto inline-flex items-center gap-1 text-xs underline" href={s.url} target="_blank" rel="noreferrer">
							Chargebee <ExternalLink className="h-3 w-3" />
						</a>
					)}
				</li>
			))}
		</ul>
	)
}

type DetailView = 'visit' | 'brevo' | 'replay' | 'chargebee'

function VisitDetail({ visit, initialView, onClose }: { visit: CheckoutJourney; initialView: DetailView; onClose: () => void }) {
	const [view, setView] = useState<DetailView>(initialView)

	const events = useQuery({
		queryKey: ['journey-visit', visit.sessionId],
		queryFn: () => apiFetch<{ events: JourneyEvent[] }>(`/api/admin/analytics/sessions/${visit.sessionId}`),
		enabled: view === 'visit',
	})

	return (
		<div className="fixed inset-0 z-50 flex justify-end">
			<button type="button" aria-hidden tabIndex={-1} className="absolute inset-0 bg-black/40" onClick={onClose} />
			<aside
				role="dialog"
				aria-modal
				// Wide enough for timelines and event details; wider still while a
				// recording plays, so the page inside is readable.
				className={`relative z-10 flex h-full w-full flex-col overflow-y-auto bg-surface px-5 pb-5 ${view === 'replay' ? 'max-w-6xl' : 'max-w-3xl'}`}
			>
				{/* Everything above the tab content stays pinned while the panel scrolls:
				    who this is, the visit's details, the way out and the tabs. It spans
				    the panel's side padding so scrolled content passes cleanly under it. */}
				<div className="sticky top-0 z-20 -mx-5 mb-4 shrink-0 border-b border-line bg-surface px-5 pt-4">
					<div className="mb-2 flex items-start justify-between gap-3">
						<div className="min-w-0">
							<h2 className="truncate font-display text-lg font-semibold">{visit.email ?? `Visit from ${visit.source}`}</h2>
							<p className="text-xs text-ink-soft">
								Came from {visit.source} · <RybbitLink visit={visit} />
							</p>
						</div>
						<button
							type="button"
							onClick={onClose}
							aria-label="Close"
							title="Close"
							className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-line bg-white text-ink hover:bg-canvas"
						>
							<X className="h-4 w-4" />
						</button>
					</div>

					<dl className="mb-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
						<div><dt className="text-xs text-ink-soft">Started</dt><dd>{formatDate(visit.startedAt)}</dd></div>
						<div><dt className="text-xs text-ink-soft">Got to</dt><dd><Badge tone={STAGE_TONES[visit.furthest]}>{STAGE_LABELS[visit.furthest]}</Badge></dd></div>
						<div className="min-w-0"><dt className="text-xs text-ink-soft">Referrer</dt><dd className="truncate">{visit.referrer ?? 'none (direct)'}</dd></div>
						<div className="min-w-0"><dt className="text-xs text-ink-soft">Campaign</dt><dd className="truncate">{visit.utmCampaign ?? '—'}</dd></div>
						<div><dt className="text-xs text-ink-soft">Device</dt><dd>{visit.device ?? '—'}</dd></div>
						<div><dt className="text-xs text-ink-soft">Country</dt><dd>{visit.country ?? '—'}</dd></div>
					</dl>

					{/* No overflow scrolling here: the tabs' -1px underline overlap made it
					    show a stray scrollbar. They wrap on narrow screens instead. */}
					<div className="flex flex-wrap gap-1">
						{(
							[
								['visit', 'Rybbit timeline'],
								['brevo', 'Brevo timeline'],
								['replay', 'Replay'],
								['chargebee', 'Chargebee'],
							] as const
						).map(([key, label]) => (
							<button
								key={key}
								type="button"
								disabled={((key === 'brevo' || key === 'chargebee') && !visit.email) || (key === 'replay' && !visit.hasReplay)}
								title={
									(key === 'brevo' || key === 'chargebee') && !visit.email
										? 'Needs an email — none was typed in the checkout during this visit'
										: key === 'replay' && !visit.hasReplay
											? 'Rybbit has no recording of this visit'
											: undefined
								}
								onClick={() => setView(key)}
								className={`-mb-px shrink-0 cursor-pointer whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40 ${
									view === key ? 'border-brand text-ink' : 'border-transparent text-ink-soft hover:text-ink'
								}`}
							>
								{label}
							</button>
						))}
					</div>
				</div>

				{view === 'replay' && visit.hasReplay ? (
					<ReplayPlayer sessionId={visit.sessionId} />
				) : view === 'brevo' && visit.email ? (
					<BrevoTab email={visit.email} />
				) : view === 'chargebee' && visit.email ? (
					<ChargebeeTab email={visit.email} />
				) : events.isPending ? (
					<Spinner label="Loading from Rybbit…" />
				) : events.error ? (
					<ErrorState error={events.error} onRetry={events.refetch} />
				) : (
					<Timeline events={events.data.events} />
				)}
			</aside>
		</div>
	)
}
